import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import FleetMap from "../components/FleetMap";
import { PageHeader, Pill } from "../components/Table";
import { Trash, Plus, MapPinLine, Path } from "@phosphor-icons/react";

export default function RoutesPage() {
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drawing, setDrawing] = useState([]);
  const [name, setName] = useState("");
  const [corridor, setCorridor] = useState("500");
  const [assign, setAssign] = useState({ vehicle: "", route: "" });
  const [bulk, setBulk] = useState({ route: "", ids: [] });

  const load = () => {
    api.get("/routes").then((r) => setRoutes(r.data.routes));
    api.get("/vehicles").then((r) => setVehicles(r.data.vehicles));
  };
  useEffect(() => { load(); }, []);

  const onMapClick = (lat, lng) => setDrawing((d) => [...d, [Number(lat.toFixed(5)), Number(lng.toFixed(5))]]);

  const create = async () => {
    if (!name) return toast.error("Route name required");
    if (drawing.length < 2) return toast.error("Click at least 2 points on the map");
    await api.post("/routes", { name, points: drawing, corridor_m: parseInt(corridor) });
    toast.success("Route created"); setName(""); setDrawing([]); load();
  };

  const remove = async (id) => { await api.delete(`/routes/${id}`); toast.success("Route deleted"); load(); };

  const bulkAssign = async () => {
    if (!bulk.route) return toast.error("Pick a route");
    if (!bulk.ids.length) return toast.error("Select vehicles");
    const r = await api.post(`/routes/${bulk.route}/bulk-assign`, { vehicle_ids: bulk.ids });
    toast.success(`Assigned ${r.data.assigned} vehicles — deviation alerts active`);
    setBulk({ route: "", ids: [] }); load();
  };

  const doAssign = async () => {
    if (!assign.vehicle) return toast.error("Pick a vehicle");
    await api.post(`/vehicles/${assign.vehicle}/assign-route`, { route_id: assign.route || null });
    toast.success(assign.route ? "Route assigned — deviation alerts active" : "Route unassigned");
    load();
  };

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Routes" subtitle="Draw routes and get instant deviation alerts" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border rounded-sm overflow-hidden h-[520px] relative">
          <FleetMap vehicles={[]} routes={routes} trail={drawing.length ? drawing : null}
            onMapClick={onMapClick} center={[19.2, 73.2]} zoom={8} />
          <div className="absolute top-2 left-2 z-[1000] bg-popover/90 border border-border rounded-sm px-3 py-1.5 text-xs flex items-center gap-2">
            <MapPinLine size={14} weight="bold" className="text-primary" />
            {drawing.length ? `${drawing.length} points — click to add more` : "Click on the map to draw a route"}
            {drawing.length > 0 && <button data-testid="clear-draw" onClick={() => setDrawing([])} className="text-destructive ml-1">clear</button>}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-card border border-border rounded-sm p-4 space-y-2">
            <div className="label-caps mb-1">New Route</div>
            <input data-testid="route-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Route name" className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm" />
            <div className="flex items-center gap-2">
              <input data-testid="route-corridor" value={corridor} onChange={(e) => setCorridor(e.target.value)} placeholder="Corridor (m)" className="flex-1 bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              <span className="text-xs text-muted-foreground">± {corridor}m</span>
            </div>
            <button data-testid="route-create" onClick={create} className="w-full flex items-center justify-center gap-1.5 bg-primary text-white font-semibold text-sm py-2 rounded-sm"><Plus size={15} weight="bold" /> Save Route</button>
          </div>

          <div className="bg-card border border-border rounded-sm p-4 space-y-2">
            <div className="label-caps mb-1">Assign Route to Vehicle</div>
            <select data-testid="assign-vehicle" value={assign.vehicle} onChange={(e) => setAssign({ ...assign, vehicle: e.target.value })} className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm">
              <option value="">Select vehicle…</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
            </select>
            <select data-testid="assign-route" value={assign.route} onChange={(e) => setAssign({ ...assign, route: e.target.value })} className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm">
              <option value="">Unassign / none</option>
              {routes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <button data-testid="assign-btn" onClick={doAssign} className="w-full bg-secondary text-white font-semibold text-sm py-2 rounded-sm">Apply</button>
          </div>

          <div className="bg-card border border-border rounded-sm p-4 space-y-2">
            <div className="label-caps mb-1">Route Library — Bulk Assign</div>
            <select data-testid="bulk-route" value={bulk.route} onChange={(e) => setBulk({ ...bulk, route: e.target.value })} className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm">
              <option value="">Select route to assign…</option>
              {routes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <div className="max-h-[130px] overflow-y-auto border border-border rounded-sm p-1">
              {vehicles.map((v) => (
                <label key={v.id} className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-white/5">
                  <input type="checkbox" data-testid={`bulk-veh-${v.vehicle_number}`}
                    checked={bulk.ids.includes(v.id)}
                    onChange={(e) => setBulk((b) => ({ ...b, ids: e.target.checked ? [...b.ids, v.id] : b.ids.filter((x) => x !== v.id) }))} />
                  {v.vehicle_number}
                </label>
              ))}
            </div>
            <button data-testid="bulk-assign-btn" onClick={bulkAssign}
              className="w-full bg-primary text-white font-semibold text-sm py-2 rounded-sm">Assign {bulk.ids.length} vehicle(s)</button>
          </div>

          <div className="bg-card border border-border rounded-sm p-2 max-h-[180px] overflow-y-auto">
            {routes.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">No routes yet</div>}
            {routes.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-2 py-2 border-b border-border/50 last:border-0 text-sm">
                <span className="flex items-center gap-1.5"><Path size={14} className="text-[#FF9500]" /><span className="font-semibold">{r.name}</span>
                  <Pill color="#5AC8FA">{r.assigned_count} veh</Pill></span>
                <button data-testid={`route-delete-${r.id}`} onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
